import ProtectedRoute, { ProtectedTypes } from '@components/ProtectedRoute';
import ScrollToTop from '@components/ScrollToTop';
import AuthContextProvider from '@context/AuthContext';
import CheckYourInboxPage from '@pages/Authentication/CheckYourInbox';
import LoginPage from '@pages/Authentication/LoginPage';
import LogoutPage from '@pages/Authentication/LogoutPage';
import VerifyToken from '@pages/Authentication/VerifyToken';
import RegistrationPage from '@pages/Register';
import NotFound from '@pages/errors/NotFound';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import CreateStarContextProvider from '@context/CreateStarContext';
import { GlobalStateProvider } from '@context/GlobalStore';
import SecondaryNavigationContextProvider from '@context/SecondaryNavigationContext';
import { getToken } from '@api/config';
import { useUserDetails } from '@api/userApiHandler';
import { applyPlatformTheme } from '@helpers/platformTheme.helper';
import { syncAdminPermissionsToLocalStorage } from '@helpers/adminPermissions.helper';
import { isTraumaFeatureEnabled } from '@config/traumaFeature';
import { logoutUser } from '@helpers/auth.helper';
import { updateOneSignalDetails } from '@helpers/median.helper';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { LoadingDots } from '@shared/atoms';
import { FlourishClickLearningBanner } from '@shared/organisms/FlourishClickLearningBanner/FlourishClickLearningBanner';
import { CompanyTypes, UserRoles } from 'custom.d';
import { RootState } from 'store';
import { getCompanyConfigurations } from 'store/Actions/CompanyConfigAction';
import { getUserStatusData } from 'store/Actions/UserStatusAction';

/** Lazy-load a named export as a route component. */
const lazyNamed = <T extends React.ComponentType<any>>(
  factory: () => Promise<Record<string, unknown>>,
  exportName: string,
) =>
  lazy(async () => {
    const module = await factory();
    const Component = module[exportName] as T | undefined;

    if (!Component) {
      throw new Error(`Missing export "${exportName}" for lazy route`);
    }

    return { default: Component };
  });

// Keep auth pages eager. All authenticated screens (including home) load on navigation.
const WellbeingDashboard = lazyNamed(
  () => import('@shared/pages/WellbeingDashboard/WellbeingDashboard'),
  'WellbeingDashboard',
);
const DeepDiveStarPage = lazy(() => import('@pages/DeepDiveStar'));
const WelcomePage = lazy(() => import('@pages/Welcome'));
const IntroductionPage = lazy(() => import('@pages/WelcomeIntroduction'));
const CompleteRegistrationPage = lazy(
  () => import('@pages/CompleteRegistration/CompleteRegistrationPage'),
);
const EmployerUsersPage = lazy(() => import('@pages/Employer/Employees'));
const EmployerCommentsPage = lazy(() => import('@pages/Employer/EmployerComments'));
const EmployerFreedomToSpeakUpPage = lazy(() => import('@pages/Employer/EmployerFreedomToSpeakUp'));
const EmployerFreedomConcernDetailPage = lazy(
  () => import('@pages/Employer/EmployerFreedomToSpeakUp/EmployerFreedomConcernDetailPage'),
);
const EmployerTraumaPage = lazy(() => import('@pages/Employer/EmployerTrauma'));
const EmployerTraumaAddStaffPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaAddStaffPage'),
);
const EmployerTraumaIncidentDetailPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaIncidentDetailPage'),
);
const EmployerTraumaIncidentsPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaIncidentsPage'),
);
const EmployerTraumaInvitationsSentPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaInvitationsSentPage'),
);
const EmployerTraumaLogIncidentPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaLogIncidentPage'),
);
const EmployerTraumaReferralDetailPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaReferralDetailPage'),
);
const EmployerTraumaRespondPage = lazy(
  () => import('@pages/Employer/EmployerTrauma/EmployerTraumaRespondPage'),
);
const EmployerExpertCreditsPage = lazy(() => import('@pages/Employer/EmployerExpertCredits'));
const FeedbackAndSuggestions = lazy(() => import('@pages/FeedbackAndSuggestions'));
const Privacy = lazy(() => import('@pages/Policies/Privacy/Privacy'));
const Terms = lazy(() => import('@pages/Policies/Terms/Terms'));
const WhatIsHapstar = lazy(() => import('@pages/WhatIsHapstar/WhatIsHapstar'));
const ParticipantReportHomeV2 = lazy(() => import('@pages/Advisor/ParticipantReportHomeV2'));
const EmailSubscription = lazy(() => import('@pages/EmailSubscription'));
const EmployeeAdvisorRequests = lazy(() => import('@pages/Employer/EmployeeAdvisorRequests'));
const EmployerAnalytics = lazy(() => import('@pages/Employer/EmployerAnalytics'));
const EmployerCommunityPage = lazy(() => import('@pages/Employer/EmployerCommunity'));
const EmployerAnalyticsV2 = lazy(() => import('@pages/Employer/EmployerAnalyticsV2'));
const IndicatorDetailReport = lazy(() => import('@pages/Employer/IndicatorDetailReport'));
const EmployerLanding = lazy(() => import('@pages/Employer/EmployerLanding'));
const EmployerLearningConfig = lazy(() => import('@pages/Management/LearningConfig'));
const ManagementLanding = lazy(() => import('@pages/Management/ManagementLanding'));
const RequestExpertBooking = lazy(() => import('@pages/RequestExpertBooking'));
const SharingLink = lazy(() => import('@pages/SharingLink'));

const AssessmentScoresPage = lazyNamed(
  () => import('@shared/pages/AssessmentScores'),
  'AssessmentScoresPage',
);
const ReflectionPage = lazyNamed(() => import('@shared/pages/Reflection'), 'ReflectionPage');
const SharePage = lazyNamed(() => import('@shared/pages/SharePage/SharePage'), 'SharePage');
const CommunityPostPage = lazyNamed(
  () => import('@shared/pages/SharePage/CommunityPostPage'),
  'CommunityPostPage',
);
const FreedomToSpeakUpPage = lazyNamed(
  () => import('@shared/pages/FreedomToSpeakUp'),
  'FreedomToSpeakUpPage',
);
const SharedThoughtsAndSpeakUpsPage = lazyNamed(
  () => import('@shared/pages/SharedThoughtsAndSpeakUpsPage'),
  'SharedThoughtsAndSpeakUpsPage',
);
const AllExperts = lazyNamed(
  () => import('@shared/pages/Experts/AllExperts/AllExperts'),
  'AllExperts',
);
const BookingRequests = lazyNamed(
  () => import('@shared/pages/Experts/BookingRequestManagement/BookingRequests/BookingRequests'),
  'BookingRequests',
);
const CreateStarRedirect = lazyNamed(
  () => import('@shared/pages/CreateStarRedirect/CreateStarRedirect'),
  'CreateStarRedirect',
);
const EarlyWarningWellbeingHub = lazyNamed(
  () => import('@shared/pages/EarlyWarningWellbeingHub/EarlyWarningWellbeingHub'),
  'EarlyWarningWellbeingHub',
);
const Expert = lazyNamed(() => import('@shared/pages/Experts/Expert/Expert'), 'Expert');
const ExpertsManagement = lazyNamed(
  () => import('@shared/pages/Experts/ExpertsManagement/ExpertsManagement'),
  'ExpertsManagement',
);
const LearningArticle = lazyNamed(
  () => import('@shared/pages/Learning/LearningArticle/LearningArticle'),
  'LearningArticle',
);
const Learnings = lazyNamed(
  () => import('@shared/pages/Learning/Learnings/Learnings'),
  'Learnings',
);
const MyJourneyPage = lazyNamed(
  () => import('@shared/pages/MyJourney/MyJourneyPage'),
  'MyJourneyPage',
);
const OnboardingAssessmentPage = lazyNamed(
  () => import('@shared/pages/OnboardingAssessmentPage/OnboardingAssessmentPage'),
  'OnboardingAssessmentPage',
);
const PlanPage = lazyNamed(() => import('@shared/pages/PlanPage/PlanPage'), 'PlanPage');
const QuestionnairePage = lazyNamed(
  () => import('@shared/pages/Questionnaire/QuestionnairePage'),
  'QuestionnairePage',
);
const ReflectionsPage = lazyNamed(
  () => import('@shared/pages/Reflections/ReflectionsPage'),
  'ReflectionsPage',
);
const TraumaPage = lazyNamed(() => import('@shared/pages/Trauma/TraumaPage'), 'TraumaPage');
const TraumaContinuePage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaContinuePage'),
  'TraumaContinuePage',
);
const TraumaDefaultPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaDefaultPage'),
  'TraumaDefaultPage',
);
const TraumaSupportResourcesPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaSupportResourcesPage'),
  'TraumaSupportResourcesPage',
);
const TraumaReturningUserPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaReturningUserPage'),
  'TraumaReturningUserPage',
);
const TraumaLogIncidentPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaLogIncidentPage'),
  'TraumaLogIncidentPage',
);
const TraumaSpecificIncidentDetailsPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaSpecificIncidentDetailsPage'),
  'TraumaSpecificIncidentDetailsPage',
);
const TraumaSpecificIncidentAssessmentDelayPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaSpecificIncidentAssessmentDelayPage'),
  'TraumaSpecificIncidentAssessmentDelayPage',
);
const TraumaSpecificIncidentScreeningPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaSpecificIncidentScreeningPage'),
  'TraumaSpecificIncidentScreeningPage',
);
const TraumaSpecificIncidentScreeningQuestionsPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaSpecificIncidentScreeningQuestionsPage'),
  'TraumaSpecificIncidentScreeningQuestionsPage',
);
const TraumaSpecificIncidentDonePage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaSpecificIncidentDonePage'),
  'TraumaSpecificIncidentDonePage',
);
const TraumaOngoingFeelingsPreAssessmentPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaOngoingFeelingsPreAssessmentPage'),
  'TraumaOngoingFeelingsPreAssessmentPage',
);
const TraumaOngoingFeelingsAssessmentPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaOngoingFeelingsAssessmentPage'),
  'TraumaOngoingFeelingsAssessmentPage',
);
const TraumaOngoingFeelingsAssessmentQuestionsPage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaOngoingFeelingsAssessmentQuestionsPage'),
  'TraumaOngoingFeelingsAssessmentQuestionsPage',
);
const TraumaOngoingFeelingsDonePage = lazyNamed(
  () => import('@shared/pages/Trauma/TraumaOngoingFeelingsDonePage'),
  'TraumaOngoingFeelingsDonePage',
);

const RouteFallback = () => {
  const { isDesktop } = useWindowDimensions();

  return (
    <div className="initialLoadingContainer">
      <LoadingDots size={isDesktop ? 24 : 20} isHapstarLoading dotCount={3} />
    </div>
  );
};

const App: React.FunctionComponent = () => {
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const redirectPath = localStorage.getItem('redirectPath');
  const user = localStorage.getItem('user');
  const parsedUser = JSON.parse(user || '{}');
  const dispatch = useAppDispatch();
  const token = getToken();
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);
  const isTraumaEnabled =
    Boolean(companyConfigData.data) && isTraumaFeatureEnabled(companyConfigData.data.traumaEnabled);
  const isLoading = useAppSelector((state: RootState) => state.userStatus.loading);
  const isStatusError = useAppSelector((state: RootState) => state.userStatus.error);
  const isCompanyConfigError = useAppSelector((state: RootState) => state.companyConfig.error);
  const isInstantHelpError = useAppSelector((state: RootState) => state.instantHelp.error);
  const isStatusLoaded = useAppSelector((state: RootState) => state.userStatus.isLoaded);
  const { isDesktop } = useWindowDimensions();
  const company = localStorage.getItem('company');
  const parsedCompany = JSON.parse(company || '{}');
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: currentUserDetails } = useUserDetails(user);

  useEffect(() => {
    if (!isLoading && !companyConfigData.loading && isStatusLoaded && companyConfigData.isLoaded) {
      setIsInitialized(true);
    }
  }, [isLoading, isStatusLoaded, companyConfigData.isLoaded, companyConfigData.loading]);

  useEffect(() => {
    if (token) {
      updateOneSignalDetails();
    }
  }, [token]);

  useEffect(() => {
    if (token && !isStatusLoaded) {
      dispatch(getUserStatusData());
    }
  }, [dispatch, token, isStatusLoaded]);

  useEffect(() => {
    if (token && !companyConfigData.isLoaded) {
      dispatch(getCompanyConfigurations());
    }
  }, [dispatch, companyConfigData.isLoaded, token]);

  useEffect(() => {
    if (isInitialized && (isStatusError || isCompanyConfigError || isInstantHelpError)) {
      logoutUser();
    }
  }, [isInitialized, isStatusError, isCompanyConfigError, isInstantHelpError]);

  useEffect(() => {
    if (currentUserDetails) {
      syncAdminPermissionsToLocalStorage(currentUserDetails);
    }
  }, [currentUserDetails]);

  useEffect(() => {
    if (companyConfigData.isLoaded) {
      applyPlatformTheme(companyConfigData.data);
    }
  }, [companyConfigData.isLoaded, companyConfigData.data]);

  const restrictedPaths = ['/signup', '/login', '/mobileapp'];

  const currentPath = window.location.pathname.replace(/\/$/, '');

  if (isAuthenticated && restrictedPaths.includes(currentPath)) {
    if (redirectPath) {
      if (redirectPath.charAt(0) === '/') {
        window.location.replace(redirectPath);
      } else {
        window.location.replace(`/${redirectPath}`);
      }
    } else {
      window.location.replace(`/`);
    }
  }

  if (token) {
    if (isLoading || !isStatusLoaded) {
      return (
        <div className="initialLoadingContainer">
          <LoadingDots size={isDesktop ? 24 : 20} isHapstarLoading dotCount={3} />
        </div>
      );
    }

    if (isStatusError || isCompanyConfigError || isInstantHelpError) {
      logoutUser();
    }
  }

  return (
    <Router>
      <ScrollToTop />
      <HelmetProvider>
        <AuthContextProvider>
          <GlobalStateProvider>
            <SecondaryNavigationContextProvider>
              <CreateStarContextProvider>
                <FlourishClickLearningBanner />
                <Suspense fallback={<RouteFallback />}>
                  <Switch>
                    <ProtectedRoute path="/" exact component={WellbeingDashboard} />

                    <ProtectedRoute
                      exact
                      path="/employer/homepage"
                      component={EmployerLanding}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/analytics"
                      component={EmployerAnalytics}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/analyticsv3"
                      component={EmployerAnalyticsV2}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      allowSubClientAnalytics
                    />
                    <ProtectedRoute
                      path="/employer/analyticsv3/indicators/:indicatorId"
                      component={IndicatorDetailReport}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      allowSubClientAnalytics
                    />
                    <ProtectedRoute
                      path="/employer/users"
                      component={EmployerUsersPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      path="/employer/comments"
                      component={EmployerCommentsPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/freedom-to-speak-up/:concernId"
                      component={EmployerFreedomConcernDetailPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={
                        Boolean(companyConfigData.data) &&
                        Boolean(companyConfigData.data.freedomToSpeakUpEnabled)
                      }
                    />
                    <ProtectedRoute
                      path="/employer/freedom-to-speak-up"
                      component={EmployerFreedomToSpeakUpPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={
                        Boolean(companyConfigData.data) &&
                        Boolean(companyConfigData.data.freedomToSpeakUpEnabled)
                      }
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/log-incident/invitations-sent"
                      component={EmployerTraumaInvitationsSentPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/log-incident/add-staff"
                      component={EmployerTraumaAddStaffPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/log-incident"
                      component={EmployerTraumaLogIncidentPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/incidents/:incidentId"
                      component={EmployerTraumaIncidentDetailPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/incidents"
                      component={EmployerTraumaIncidentsPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/:referralId/respond"
                      component={EmployerTraumaRespondPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      exact
                      path="/employer/trauma/:referralId"
                      component={EmployerTraumaReferralDetailPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/employer/trauma"
                      component={EmployerTraumaPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/employer/experts"
                      component={EmployerExpertCreditsPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={companyConfigData.data && companyConfigData.data.expertsEnabled}
                    />

                    <ProtectedRoute
                      path="/employer/talk-to-advisor-requests"
                      component={EmployeeAdvisorRequests}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={
                        parsedCompany?.company_type?.toLowerCase() === CompanyTypes.EMPLOYABILITY
                      }
                    />

                    <ProtectedRoute
                      path="/employer/community"
                      component={EmployerCommunityPage}
                      authorizedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                      condition={
                        companyConfigData.data && Boolean(companyConfigData.data?.communityEnabled)
                      }
                    />

                    <ProtectedRoute
                      exact
                      path="/management/learning"
                      component={EmployerLearningConfig}
                      authorizedRoles={[UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      exact
                      path="/management/homepage"
                      component={ManagementLanding}
                      authorizedRoles={[UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      exact
                      path="/management/experts"
                      component={ExpertsManagement}
                      authorizedRoles={[UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      exact
                      path="/management/booking-requests"
                      component={BookingRequests}
                      authorizedRoles={[UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute path="/stars" component={DeepDiveStarPage} />

                    <ProtectedRoute
                      path="/experts"
                      component={AllExperts}
                      condition={companyConfigData.data && companyConfigData.data.expertsEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma"
                      exact
                      component={TraumaPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/continue"
                      exact
                      component={TraumaContinuePage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/support-resources"
                      exact
                      component={TraumaSupportResourcesPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma-default"
                      exact
                      component={TraumaDefaultPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/ongoing-feelings/done"
                      exact
                      component={TraumaOngoingFeelingsDonePage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/ongoing-feelings/assessment/questions"
                      exact
                      component={TraumaOngoingFeelingsAssessmentQuestionsPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/ongoing-feelings/assessment"
                      exact
                      component={TraumaOngoingFeelingsAssessmentPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/ongoing-feelings/pre-assessment"
                      exact
                      component={TraumaOngoingFeelingsPreAssessmentPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/specific-incident/done"
                      exact
                      component={TraumaSpecificIncidentDonePage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/specific-incident/screening/delay"
                      exact
                      component={TraumaSpecificIncidentAssessmentDelayPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/specific-incident/screening/questions"
                      exact
                      component={TraumaSpecificIncidentScreeningQuestionsPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/specific-incident/screening"
                      exact
                      component={TraumaSpecificIncidentScreeningPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident/specific-incident/details"
                      exact
                      component={TraumaSpecificIncidentDetailsPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma/log-incident"
                      exact
                      component={TraumaLogIncidentPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/trauma-returning-user"
                      exact
                      component={TraumaReturningUserPage}
                      condition={isTraumaEnabled}
                    />
                    <ProtectedRoute
                      path="/expert/:expertId"
                      component={Expert}
                      condition={companyConfigData.data && companyConfigData.data.expertsEnabled}
                    />
                    <ProtectedRoute
                      path="/welcome"
                      component={WelcomePage}
                      RedirectFunction={ProtectedTypes.AGREE_TERMS}
                    />

                    <ProtectedRoute
                      path="/introduction"
                      component={IntroductionPage}
                      RedirectFunction={ProtectedTypes.JOIN}
                    />

                    <ProtectedRoute
                      path="/onboarding-assessment"
                      component={OnboardingAssessmentPage}
                      RedirectFunction={ProtectedTypes.ON_BOARDING_ASSESSMENT}
                    />

                    <ProtectedRoute path="/create-a-star" component={CreateStarRedirect} />
                    <ProtectedRoute path="/new/create-a-star" component={CreateStarRedirect} />

                    <ProtectedRoute path="/about" component={WhatIsHapstar} />
                    <ProtectedRoute path="/contact" component={FeedbackAndSuggestions} />
                    <ProtectedRoute
                      path="/feedback-and-suggestions"
                      component={FeedbackAndSuggestions}
                    />
                    <ProtectedRoute
                      path="/request-expert-session"
                      component={RequestExpertBooking}
                    />

                    <ProtectedRoute
                      path="/advisor/participant-report"
                      component={ParticipantReportHomeV2}
                      authorizedRoles={[UserRoles.ADVISOR, UserRoles.SUPER_ADMIN]}
                    />
                    <ProtectedRoute
                      path="/advisor/participant-reportv2"
                      component={ParticipantReportHomeV2}
                      authorizedRoles={[UserRoles.ADVISOR, UserRoles.SUPER_ADMIN]}
                    />
                    <Route path="/signup" exact component={RegistrationPage} />
                    <Route path="/signup/:companyURL" exact component={RegistrationPage} />
                    <ProtectedRoute
                      path="/complete-signup"
                      exact
                      component={CompleteRegistrationPage}
                      RedirectFunction={ProtectedTypes.REGISTERED}
                    />
                    <ProtectedRoute
                      path="/wellbeing"
                      exact
                      component={EarlyWarningWellbeingHub}
                      condition={companyConfigData.data && companyConfigData.data.showInstantHelp}
                    />

                    <Route path="/login" exact component={LoginPage} />
                    <Route path="/login/:companyURL" exact component={LoginPage} />
                    <Route
                      path="/login/check-your-inbox/:email"
                      exact
                      component={CheckYourInboxPage}
                    />
                    <Route
                      path="/login/check-your-inbox/:email/:companyURL"
                      exact
                      component={CheckYourInboxPage}
                    />
                    <Route path="/login/verify/:userId/:token" component={VerifyToken} />
                    <Route path="/mobileapp" exact component={LoginPage} />
                    <Route path="/email-subscription" component={EmailSubscription} />
                    <ProtectedRoute path="/logout" exact component={LogoutPage} />
                    <Route path="/link/:companyURL" component={SharingLink} />

                    <ProtectedRoute path="/assessment" exact component={QuestionnairePage} />

                    <ProtectedRoute path="/journal" exact component={ReflectionPage} />

                    <ProtectedRoute path="/posts" component={Learnings} />
                    <ProtectedRoute path="/post/:postId" component={LearningArticle} />

                    <ProtectedRoute
                      path="/shared-thoughts-and-speak-ups"
                      exact
                      component={SharedThoughtsAndSpeakUpsPage}
                    />

                    <ProtectedRoute path="/journals" exact component={ReflectionsPage} />

                    <ProtectedRoute path="/engagement" exact component={MyJourneyPage} />
                    <ProtectedRoute exact path="/improve-plans" component={PlanPage} />
                    <ProtectedRoute
                      exact
                      path="/assessment-scores"
                      component={AssessmentScoresPage}
                    />

                    <ProtectedRoute exact path="/share" component={SharePage} />
                    <ProtectedRoute
                      exact
                      path="/share/community/posts/:postId"
                      component={CommunityPostPage}
                      condition={
                        Boolean(companyConfigData.data) &&
                        Boolean(companyConfigData.data?.communityEnabled)
                      }
                    />

                    <ProtectedRoute
                      exact
                      path="/freedom-to-speak-up"
                      component={FreedomToSpeakUpPage}
                      condition={
                        Boolean(companyConfigData.data) &&
                        Boolean(companyConfigData.data.freedomToSpeakUpEnabled)
                      }
                    />

                    <Route path="/policies/terms/" exact component={Terms} />
                    <Route path="/policies/privacy/" exact component={Privacy} />

                    <Route path="/404" component={NotFound} />
                    <Route path="*" component={NotFound} />
                  </Switch>
                </Suspense>
              </CreateStarContextProvider>
            </SecondaryNavigationContextProvider>
          </GlobalStateProvider>
        </AuthContextProvider>
      </HelmetProvider>
    </Router>
  );
};

export default App;
